/**
 * Display helpers for SRP vehicle field values on detail/edit screens.
 *
 * Chrome/Data Hub tech specs are often `{ name, value, unit? }[]`. A bad prefill
 * mapping can also stash that whole array into numeric MPG fields. These helpers
 * recover a usable display value instead of rendering "[object Object], …".
 */

export type ChromeTechSpec = {
  name?: unknown
  value?: unknown
  unit?: unknown
  Title?: unknown
  Value?: unknown
}

const MPG_FIELD_TECH_SPEC_NAMES: Record<string, readonly string[]> = {
  mpg_city_average: ['Fuel economy city'],
  mpg_city_low: ['Fuel economy city'],
  mpg_city_high: ['Fuel economy city'],
  mpg_highway_average: ['Fuel economy highway'],
  mpg_highway_low: ['Fuel economy highway'],
  mpg_highway_high: ['Fuel economy highway'],
  engine_displacement: ['Displacement liters'],
}

function normalizeSpecName(raw: unknown): string {
  return String(raw ?? '')
    .trim()
    .toLowerCase()
}

export function isChromeTechSpecLike(value: unknown): value is ChromeTechSpec {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const record = value as Record<string, unknown>
  return (
    record.name != null ||
    record.value != null ||
    record.Title != null ||
    record.Value != null
  )
}

export function isChromeTechSpecArray(value: unknown): value is ChromeTechSpec[] {
  return Array.isArray(value) && value.length > 0 && value.every(isChromeTechSpecLike)
}

function parseTechSpecsPayload(value: unknown): ChromeTechSpec[] | null {
  if (isChromeTechSpecArray(value)) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed.startsWith('[')) return null
    try {
      const parsed = JSON.parse(trimmed)
      return isChromeTechSpecArray(parsed) ? parsed : null
    } catch {
      return null
    }
  }
  return null
}

function techSpecLabel(spec: ChromeTechSpec): string {
  return String(spec.name ?? spec.Title ?? '').trim()
}

function techSpecRawValue(spec: ChromeTechSpec): unknown {
  return spec.value ?? spec.Value
}

function formatTechSpecLine(spec: ChromeTechSpec): string {
  const name = techSpecLabel(spec)
  const raw = techSpecRawValue(spec)
  const unit = String(spec.unit ?? '').trim()
  const value = raw == null || raw === '' ? '' : String(raw)
  if (!name && !value) return ''
  if (!name) return unit ? `${value} ${unit}` : value
  if (!value) return name
  return unit ? `${name}: ${value} ${unit}` : `${name}: ${value}`
}

export function formatChromeTechSpecs(value: unknown): string {
  const specs = parseTechSpecsPayload(value)
  if (!specs) return ''
  return specs.map(formatTechSpecLine).filter(Boolean).join('\n')
}

function coerceNumericSpecValue(raw: unknown): number | string | null {
  if (raw == null || raw === '') return null
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  const asString = String(raw).trim()
  if (!asString) return null
  const numeric = parseFloat(asString.replace(/[^\d.-]/g, ''))
  return Number.isFinite(numeric) ? numeric : asString
}

/**
 * If a numeric SRP field was incorrectly populated with a TechSpecs array
 * (or JSON string of one), extract the matching scalar value.
 */
export function extractScalarFromTechSpecsField(field: string, value: unknown): unknown {
  const names = MPG_FIELD_TECH_SPEC_NAMES[field]
  if (!names) return value

  const specs = parseTechSpecsPayload(value)
  if (!specs) return value

  const wanted = new Set(names.map((n) => n.toLowerCase()))
  const match = specs.find((spec) => wanted.has(normalizeSpecName(techSpecLabel(spec))))
  if (!match) return null
  return coerceNumericSpecValue(techSpecRawValue(match))
}

/**
 * Normalize a field value before binding to a number/text input or detail label.
 */
export function normalizeVehicleFieldForDisplay(field: string, value: unknown): unknown {
  if (value == null || value === '') return value

  if (field === 'tech_specs') {
    const specs = parseTechSpecsPayload(value)
    return specs ?? value
  }

  if (MPG_FIELD_TECH_SPEC_NAMES[field]) {
    return extractScalarFromTechSpecsField(field, value)
  }

  // Generic recovery: object arrays in number-ish fields are never valid scalars.
  if (isChromeTechSpecArray(value) && field.startsWith('mpg_')) {
    return extractScalarFromTechSpecsField(field, value)
  }

  return value
}

/**
 * Human-readable string for Vehicle Details (and similar read-only views).
 */
export function formatVehicleFieldDisplayValue(field: string, value: unknown): string {
  const normalized = normalizeVehicleFieldForDisplay(field, value)

  if (normalized == null || normalized === '') return '—'
  if (typeof normalized === 'boolean') return normalized ? 'Yes' : 'No'
  if (typeof normalized === 'number') return String(normalized)

  if (field === 'tech_specs' || isChromeTechSpecArray(normalized)) {
    const formatted = formatChromeTechSpecs(normalized)
    return formatted || '—'
  }

  if (typeof normalized === 'string') {
    const asSpecs = parseTechSpecsPayload(normalized)
    if (asSpecs) {
      const formatted = formatChromeTechSpecs(asSpecs)
      return formatted || '—'
    }
    return normalized
  }

  if (Array.isArray(normalized)) {
    if (normalized.length === 0) return '—'
    if (normalized.every((item) => item == null || ['string', 'number', 'boolean'].includes(typeof item))) {
      return normalized.map(String).join(', ')
    }
    // Structured objects that aren't Chrome tech specs — pretty JSON.
    try {
      return JSON.stringify(normalized, null, 2)
    } catch {
      return String(normalized)
    }
  }

  if (typeof normalized === 'object') {
    try {
      return JSON.stringify(normalized, null, 2)
    } catch {
      return String(normalized)
    }
  }

  return String(normalized)
}

/**
 * Normalize a full vehicle payload for form/detail display.
 * Recovers numeric MPG/displacement scalars from Chrome TechSpecs arrays and
 * stringifies tech_specs for textarea binding.
 */
export function sanitizeVehicleRecordForDisplay(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...data }
  const techSpecs = out.tech_specs

  for (const key of Object.keys(out)) {
    const normalized = normalizeVehicleFieldForDisplay(key, out[key])
    if (
      (normalized == null || normalized === '') &&
      techSpecs != null &&
      (key === 'engine_displacement' || key.startsWith('mpg_'))
    ) {
      out[key] = normalizeVehicleFieldForDisplay(key, techSpecs)
      continue
    }
    out[key] = normalized
  }

  if (out.tech_specs != null && typeof out.tech_specs !== 'string') {
    const formatted = formatChromeTechSpecs(out.tech_specs)
    out.tech_specs = formatted || JSON.stringify(out.tech_specs, null, 2)
  }

  return out
}
